import { useState } from "react";
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Typography,
  App,
  Skeleton,
  Tooltip,
} from "antd";

import styles from "./nspd-registry-json-preview.module.css";
import { getNSPDRawJson } from "@/api/nspd";
import CheckIcon from "@/icons/CheckIcon";
import DuplicateIcon from "@/icons/DuplicateIcon";

const { Text, Title } = Typography;

const NSPDRegistryJsonPreview = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [error, setError] = useState(null);

  const [json, setJson] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const formattedJson = json ? JSON.stringify(json, null, 2) : "";

  const handleReset = () => {
    setJson(null);
    setError(null);
    setIsCopied(false);
    form.resetFields();
  };

  const handleCopyJson = async () => {
    if (!formattedJson) return;

    try {
      await navigator.clipboard.writeText(formattedJson);
      setIsCopied(true);
      message.success("JSON скопирован");

      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    } catch (error) {
      message.error("Не удалось скопировать JSON");
    }
  };

  const handleGetJson = async ({ cadastre_number }) => {
    setError(null);
    try {
      setIsLoading(true);
      setJson(null);

      const response = await getNSPDRawJson(cadastre_number.trim(), true);

      setJson(response);
    } catch (error) {
      const errorMessage =
        error?.message || "Не удалось получить JSON из реестра";

      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <Flex vertical gap={16}>
        <Title level={4} style={{ margin: 0 }}>
          Получение JSON из НСПД
        </Title>

        <Text type="secondary">
          Введите кадастровый номер, чтобы выполнить тестовый запрос в реестр.
          Ответ будет показан без сохранения данных.
        </Text>

        <Form form={form} layout="inline" onFinish={handleGetJson}>
          <Form.Item
            name="cadastre_number"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Введите кадастровый номер",
              },
            ]}
          >
            <Input style={{ width: 400 }} allowClear disabled={isLoading} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              Получить JSON
            </Button>
          </Form.Item>

          {(json || error) && (
            <Form.Item>
              <Button onClick={handleReset} disabled={isLoading}>
                Очистить
              </Button>
            </Form.Item>
          )}
        </Form>

        {error && <Text type="danger">{error}</Text>}

        {json && (
          <div className={styles.jsonPreview}>
            <div className={styles.jsonPreviewToolbar}>
              <Tooltip title={isCopied ? "Скопировано" : "Копировать JSON"}>
                <Button
                  type="default"
                  icon={isCopied ? <CheckIcon /> : <DuplicateIcon />}
                  onClick={handleCopyJson}
                  className={styles.copyButton}
                />
              </Tooltip>
            </div>

            <div className={styles.jsonPreviewBody}>
              <pre className={styles.jsonPreviewText}>{formattedJson}</pre>
            </div>
          </div>
        )}

        {isLoading && (
          <Skeleton
            style={{ width: 400 }}
            active
            title={false}
            paragraph={{ rows: 3 }}
          />
        )}
      </Flex>
    </Card>
  );
};

export default NSPDRegistryJsonPreview;
